import { useState, useEffect, useMemo, useCallback } from "react"
import { useParams, useNavigate } from "react-router-dom"
import Box from "@mui/material/Box"
import Card from "@mui/material/Card"
import Stack from "@mui/material/Stack"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import Typography from "@mui/material/Typography"
import TextField from "@mui/material/TextField"
import InputAdornment from "@mui/material/InputAdornment"
import IconButton from "@mui/material/IconButton"
import CircularProgress from "@mui/material/CircularProgress"
import { Iconify } from "@/src/common/iconify"
import { DashboardContent } from "@/src/layouts/dashboard"
import { paths } from "@/src/routes/paths"
import { RouterLink } from "@/src/routes/components"
import {
    getLeadsByJobId,
    type InternalJobDetailResponse,
    type AdminJobLeadCandidate,
} from "@/src/services/internal"
import { useInternalStore } from "@/src/store/slices/internal.slice"
import { normalizeJobStatus, JOB_STATUS_PILL_SX, type JobStatus } from "@/src/features/internalUsers/constants"

// ----------------------------------------------------------------------

type AiTag = "Fit" | "Partial Fit" | "Not Interested" | "No Response"

type CandidateRow = {
    id: string
    leadNo: string
    name: string
    phone: string
    aiTag: string
    roundStatus: string
}

// const AI_TAG_PILL_SX: Record<AiTag, { bgcolor: string; color: string }> = {
//     Fit: { bgcolor: "success.lighter", color: "success.darker" },
//     "Partial Fit": { bgcolor: "warning.lighter", color: "warning.darker" },
//     "Not Interested": { bgcolor: "error.lighter", color: "error.darker" },
//     "No Response": { bgcolor: "grey.200", color: "text.secondary" },
// }

const DEFAULT_PAGE = 0
const DEFAULT_SIZE = 20

function normalizeAiTag(value: string | undefined): AiTag {
    if (!value) return "No Response"
    const map: Record<string, AiTag> = {
        fit: "Fit",
        "partial fit": "Partial Fit",
        "not interested": "Not Interested",
        "no response": "No Response",
    }
    return map[value.toLowerCase()] ?? "No Response"
}

function mapCandidateToRow(item: AdminJobLeadCandidate, index: number): CandidateRow {
    return {
        id: item.candidateId,
        leadNo: String(index + 1),
        name: item.candidateName || "-",
        phone: item.phoneNumber || "-",
        aiTag: item.aiTag,
        roundStatus: item.roundStatus,
    }
}

// ----------------------------------------------------------------------

export default function JobLeadsDetail() {
    const { jobId } = useParams<{ jobId: string }>()
    const navigate = useNavigate()
    const { selectedJob } = useInternalStore()
    const [searchQuery, setSearchQuery] = useState("")
    const [debouncedSearch, setDebouncedSearch] = useState("")
    const [jobDetail, setJobDetail] = useState<InternalJobDetailResponse | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    // Debounce search for API (e.g. 400ms)
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 400)
        return () => clearTimeout(t)
    }, [searchQuery])

    const fetchJob = useCallback(async () => {
        if (!jobId) return
        setLoading(true)
        setError(null)
        try {
            const res = await getLeadsByJobId(jobId, {
                page: DEFAULT_PAGE,
                size: DEFAULT_SIZE,
                search: debouncedSearch || undefined,
            })
            setJobDetail(res)
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to load job")
            setJobDetail(null)
        } finally {
            setLoading(false)
        }
    }, [jobId, debouncedSearch])

    useEffect(() => {
        fetchJob()
    }, [fetchJob])

    const data = jobDetail?.data
    const jobName = data?.jobName ?? selectedJob?.jobName ?? ""
    const workspaceName = data?.workspaceName ?? selectedJob?.workspaceName ?? ""
    const status = normalizeJobStatus(data?.status as string)
    const candidatesPage = data?.candidates
    const candidates: CandidateRow[] = useMemo(() => {
        const content = candidatesPage?.content ?? []
        return content.map((item, index) => mapCandidateToRow(item, index))
    }, [candidatesPage?.content])

    const handleBack = () => navigate(paths.internalUser.jobsAndLeads)
    const handleDeleteLead = (candidateId: string) => {
        // TODO: Call delete lead API
    }

    if (!jobId) {
        return (
            <DashboardContent sx={{ py: 5 }}>
                <Typography color="text.secondary">Job not found.</Typography>
                <IconButton component={RouterLink} href={paths.internalUser.jobsAndLeads} sx={{ mt: 1 }}>
                    <Iconify icon="eva:arrow-back-fill" />
                </IconButton>
            </DashboardContent>
        )
    }

    if (error && !jobDetail) {
        return (
            <DashboardContent sx={{ py: 5 }}>
                <Typography color="error">{error}</Typography>
                <IconButton component={RouterLink} href={paths.internalUser.jobsAndLeads} sx={{ mt: 1 }}>
                    <Iconify icon="eva:arrow-back-fill" />
                </IconButton>
            </DashboardContent>
        )
    }

    return (
        <DashboardContent sx={{ flexGrow: 1, display: "flex", flexDirection: "column", paddingBottom: "20px", mt: 3 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <IconButton onClick={handleBack} size="small" aria-label="Back to Jobs & Leads">
                    <Iconify icon="eva:arrow-back-fill" width={24} />
                </IconButton>
                <Typography variant="h4" fontWeight={700}>
                    {loading && !jobName ? "Loading…" : jobName}
                </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary">
                    {workspaceName}
                </Typography>
                <Box
                    component="span"
                    sx={{
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 10,
                        typography: "caption",
                        fontWeight: 600,
                        ...JOB_STATUS_PILL_SX[status],
                    }}
                >
                    {status}
                </Box>
            </Stack>

            <Card sx={{ overflow: "hidden", border: 1, borderColor: "divider", borderRadius: 2 }}>
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}
                >
                    <Typography variant="subtitle1" fontWeight={600}>
                        Candidates ({candidatesPage?.totalElements ?? candidates.length})
                    </Typography>
                    <TextField
                        placeholder="Search by name or lead #"
                        size="small"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        sx={{ width: 280 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Iconify icon="eva:search-fill" width={20} sx={{ color: "text.disabled" }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Stack>
                <TableContainer>
                    {loading ? (
                        <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
                            <CircularProgress />
                        </Stack>
                    ) : (
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: "grey.50" }}>
                                    <TableCell sx={{ fontWeight: 600, color: "text.secondary", py: 2 }}>
                                        Lead #
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: "text.secondary", py: 2 }}>Name</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: "text.secondary", py: 2 }}>
                                        Phone Number
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: "text.secondary", py: 2 }}>
                                        AI Tag
                                    </TableCell>

                                    <TableCell sx={{ fontWeight: 600, color: "text.secondary", py: 2 }}>
                                        Round Status
                                    </TableCell>
                                    <TableCell
                                        align="right"
                                        sx={{ fontWeight: 600, color: "text.secondary", py: 2, width: 56 }}
                                    />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {candidates.map((row) => (
                                    <TableRow
                                        key={row.id}
                                        hover
                                        sx={{
                                            "&:last-child td": { borderBottom: 0 },
                                            "& td": { py: 2, borderBottom: 1, borderColor: "divider" },
                                        }}
                                    >
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={500}>
                                                {row.leadNo}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{row.name}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary">
                                                {row.phone}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Box
                                                component="span"
                                                sx={{
                                                    display: "inline-block",
                                                    px: 1.5,
                                                    py: 0.5,
                                                    borderRadius: 10,
                                                    typography: "caption",
                                                    fontWeight: 600,
                                                }}
                                            >
                                                {row.aiTag}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary">
                                                {row.roundStatus}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </TableContainer>
            </Card>
        </DashboardContent>
    )
}
